import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { aggridHeaderClass, SmallTitleSpan } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultSAL6030_R01HeaderDto, SAL6030_R01HeaderDto } from "./Dto_SAL6030_R01";
import { useSAL6030_R01Store } from "./Store_SAL6030_R01";

export const HEADER_COL_STATE_KEY = 'SAL6030_R01-header-column-state';

interface Props {
	headerGridRef: RefObject<AgGridReact<SAL6030_R01HeaderDto> | null>;
	onHeaderRowClick: (data: SAL6030_R01HeaderDto) => void;
	/** 상단 행(LR 분할) 영역 너비 % */
	widthPct: number;
}

export const Table_SAL6030_R01 = ({ headerGridRef, onHeaderRowClick, widthPct }: Props) => {
	const {
		SAL6030_R01List,
		selectedSAL6030_R01List,
		setSelectedSAL6030_R01List,
		columnState, setColumnState,
		scrollTop,
		setScrollTop,
	} = useSAL6030_R01Store();

	const [hoveredHeader, setHoveredHeader] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: headerGridRef,
		key: "valueIndex",
		gridData: SAL6030_R01List,
		defaultGridData: defaultSAL6030_R01HeaderDto,
		selectedRow: selectedSAL6030_R01List,
	});

	const headerNames: Record<string, string> = {};

	const baseColumnDefs: ColDef<SAL6030_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "품목코드", colId: "prntItemcd", field: "prntItemcd", width: 130,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "품명", colId: "itemNm", field: "itemNm", width: 160,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 140,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "보관위치", colId: "locationNm", field: "locationNm", width: 120,
			headerClass: aggridHeaderClass,
		} as ColDef<SAL6030_R01HeaderDto>,
		{
			headerName: "모델명", colId: "modelNo", field: "modelNo", width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: "도급자재비", colId: "contractCost", field: "contractCost", width: 110,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "SMT비용", colId: "smtCost", field: "smtCost", width: 100,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
		{
			headerName: "원가합계", colId: "totalCost", field: "totalCost", width: 110,
			type: "numericColumn",
			valueFormatter: (p: { value?: number }) => p.value?.toLocaleString() ?? "0",
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL6030_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		
		return GridCustomColumnDefs<SAL6030_R01HeaderDto>({
			baseColumnDefs: baseColumnDefs,
			columnState: columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const headerPinnedBottom = useMemo(() => [
			{
				prntItemcd: SAL6030_R01List.length.toLocaleString() + "건",
				contractCost: SAL6030_R01List.reduce((s, i) => s + (i.contractCost || 0), 0).toLocaleString(),
				smtCost: SAL6030_R01List.reduce((s, i) => s + (i.smtCost || 0), 0).toLocaleString(),
				totalCost: SAL6030_R01List.reduce((s, i) => s + (i.totalCost || 0), 0).toLocaleString(),
			},
	], [SAL6030_R01List]);

	const handleHeaderClick = useCallback(
		(e: RowClickedEvent<SAL6030_R01HeaderDto>) => {
			if (e.node.rowPinned === "bottom") return;
			const row = e.data;
			if (!row) return;
			setSelectedSAL6030_R01List(row);
			onHeaderRowClick(row);
		},
		[setSelectedSAL6030_R01List, onHeaderRowClick]
	);

	return (
		<div className="h-full flex flex-col" style={{ width: `${widthPct}%` }}>
			<SmallTitleSpan>제품 목록</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<SAL6030_R01HeaderDto>
					enableCellCalculation
					gridRef={headerGridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedSAL6030_R01List(undefined)}
					defaultRowData={defaultSAL6030_R01HeaderDto}
					baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setColumnState}
					localStorageKey={HEADER_COL_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<SAL6030_R01HeaderDto>
						ref={headerGridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<SAL6030_R01HeaderDto>({
							idKey: "valueIndex",
							selectedList: selectedSAL6030_R01List?.valueIndex ? [selectedSAL6030_R01List.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleHeaderClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={headerPinnedBottom}
						setHoveredRowIndex={setHoveredHeader}
						columnStateKey={HEADER_COL_STATE_KEY}
						setColumnState={setColumnState}
						scrollTop={scrollTop}
						setScrollTop={setScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
